import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Package, 
  ArrowLeft, 
  Plus, 
  QrCode, 
  Search, 
  Trash2, 
  Copy, 
  Save, 
  Share, 
  ShoppingCart,
  User,
  MapPin,
  Loader2,
  Percent,
  TrendingUp,
  Calendar,
  Download,
  MessageCircle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { logActivity } from '@/lib/activity-log';
import { useLocation } from '@/contexts/LocationContext';
import { WorkingQRScanModal } from '@/components/WorkingQRScanModal';
import { KvkkConsentModal } from '@/components/kvkk/KvkkConsentModal';
import { supabase } from '@/integrations/supabase/client';
import type { ProductData } from '@/hooks/use-working-qr-scan';
import { 
  packageService, 
  packageItemService, 
  productService, 
  customerService, 
  packageUtils,
  type PackageWithDetails,
  type PackageInsert,
  type PackageUpdate,
  type PackageItemInsert,
  type ProductRow,
  type CustomerRow
} from '@/lib/packages';
import { 
  generatePackagePDF, 
  uploadPackagePDF, 
  sharePackagePDF,
  fetchCompanyForPDF,
  getPackagePDFUrl,
  type PackagePDFData 
} from '@/lib/pdf-generator';

interface PackageItemUI {
  id: string;
  productId: string;
  name: string;
  code: string;
  quantity: number;
  unit: string;
  price: number;
  note: string;
}

interface PackageDataUI {
  id?: string;
  title: string;
  customerId: string;
  customerName: string;
  items: PackageItemUI[];
  globalDiscount: number;
  note: string;
  status: 'draft' | 'offered' | 'accepted' | 'rejected' | 'expired' | 'converted' | 'cancelled';
  validUntil: string;
  createdAt: string;
  updatedAt: string;
}

export default function PackageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { companyId, loading: authLoading, user } = useAuth();
  const { currentLocation } = useLocation();
  const isNewPackage = id === 'new';
  const preselectedCustomerId = searchParams.get('customerId');

  const [packageData, setPackageData] = useState<PackageDataUI>({
    title: `Package ${new Date().toLocaleString()}`,
    customerId: '',
    customerName: '',
    items: [],
    globalDiscount: 0,
    note: '',
    status: 'draft',
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
    createdAt: new Date().toISOString().split('T')[0],
    updatedAt: new Date().toISOString().split('T')[0],
  });

  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);
  const [showProductSelect, setShowProductSelect] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [productCode, setProductCode] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Search states (like NewVisitModal)
  const [customerSearch, setCustomerSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [showProductResults, setShowProductResults] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // Discount calculation states
  const [discountType, setDiscountType] = useState<'amount' | 'percentage'>('amount');
  const [discountPercentage, setDiscountPercentage] = useState(0);
  
  // QR Scan state
  const [showQRScan, setShowQRScan] = useState(false);
  
  // PDF generation state
  const [generatingPDF, setGeneratingPDF] = useState(false);
  
  // SMS sending state
  const [sendingSMS, setSendingSMS] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [generatedPDFPath, setGeneratedPDFPath] = useState<string>('');
  const [companyDataForSMS, setCompanyDataForSMS] = useState<{name: string} | null>(null);
  
  // KVKK consent modal state
  const [showKvkkModal, setShowKvkkModal] = useState(false);
  const [queuedSMSData, setQueuedSMSData] = useState<{
    phone: string;
    pdfPath: string;
    companyName: string;
  } | null>(null);

  // Helper functions (like NewVisitModal)
  const getCustomerName = (customer: CustomerRow) => {
    return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Adsız Müşteri';
  };

  // Format phone number for SMS (add +90 if not present)
  const formatPhoneForSMS = (phone: string): string => {
    if (!phone) return '';
    
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    
    // If it starts with 90, add + prefix
    if (cleaned.startsWith('90')) {
      return '+' + cleaned;
    }
    
    // If it starts with 0, replace with +90
    if (cleaned.startsWith('0')) {
      return '+90' + cleaned.substring(1);
    }
    
    // If it doesn't start with country code, add +90
    if (cleaned.length === 10) {
      return '+90' + cleaned;
    }
    
    // If it already looks like it has country code but no +, add +
    if (cleaned.length > 10 && !phone.startsWith('+')) {
      return '+' + cleaned;
    }
    
    return phone;
  };

  // Format phone number for KVKK check (normalize to match database format: 90XXXXXXXXXX)
  const formatPhoneForKvkk = (phone: string): string => {
    if (!phone) return '';
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    
    console.log('📞 [KVKK Format] Input phone:', phone, 'Cleaned:', cleaned);
    
    // If it's already in database format (starts with 90 and has 12 digits), return as-is
    if (cleaned.startsWith('90') && cleaned.length === 12) {
      console.log('📞 [KVKK Format] Already in correct format:', cleaned);
      return cleaned;
    }
    
    // If it starts with 90 but has more digits (like +90...), extract the 12-digit version
    if (cleaned.startsWith('90') && cleaned.length > 12) {
      const normalized = cleaned.substring(0, 12);
      console.log('📞 [KVKK Format] Truncated to 12 digits:', normalized);
      return normalized;
    }
    
    // If it's 10 digits (Turkish mobile format like 5464337247), prepend 90
    if (cleaned.length === 10) {
      const normalized = '90' + cleaned;
      console.log('📞 [KVKK Format] 10 digits - prepended 90:', normalized);
      return normalized;
    }
    
    // If it starts with 0 followed by 10 digits (like 05464337247), remove 0 and prepend 90
    if (cleaned.startsWith('0') && cleaned.length === 11) {
      const normalized = '90' + cleaned.substring(1);
      console.log('📞 [KVKK Format] 11 digits starting with 0 - normalized:', normalized);
      return normalized;
    }
    
    // If it's 11 digits starting with 5 (mobile), prepend 90
    if (cleaned.length === 11 && cleaned.startsWith('5')) {
      const normalized = '90' + cleaned;
      console.log('📞 [KVKK Format] 11 digits starting with 5 - prepended 90:', normalized);
      return normalized;
    }
    
    // If it's 9 digits (without leading 5), prepend 905
    if (cleaned.length === 9) {
      const normalized = '905' + cleaned;
      console.log('📞 [KVKK Format] 9 digits - prepended 905:', normalized);
      return normalized;
    }
    
    // Return as-is if it doesn't match any pattern (log warning)
    console.warn('📞 [KVKK Format] Unknown format, returning as-is:', cleaned);
    return cleaned;
  };

  // Check KVKK consent status for a phone number
  const checkKvkkConsent = async (phone: string): Promise<'granted' | 'no_record' | 'denied' | 'withdrawn' | 'unknown'> => {
    try {
      const formattedPhone = formatPhoneForKvkk(phone);
      console.log('🔍 [KVKK Check] Checking consent for:', { original: phone, formatted: formattedPhone });
      
      if (!formattedPhone) {
        console.warn('🔍 [KVKK Check] Empty formatted phone');
        return 'unknown';
      }

      const { data, error } = await supabase.rpc('kvkk_get_status_for_phone', {
        p_phone: formattedPhone,
      });

      console.log('🔍 [KVKK Check] RPC response:', { data, error, formattedPhone });

      if (error) {
        console.error('❌ [KVKK Check] Error checking KVKK consent:', error);
        return 'unknown';
      }

      if (!data || data.length === 0) {
        console.log('🔍 [KVKK Check] No consent record found for:', formattedPhone);
        return 'no_record';
      }

      const consentStatus = data[0].consent_status as 'granted' | 'denied' | 'withdrawn';
      console.log('✅ [KVKK Check] Consent status found:', consentStatus, 'for phone:', formattedPhone);
      return consentStatus;
    } catch (err) {
      console.error('❌ [KVKK Check] Exception checking KVKK consent:', err);
      return 'unknown';
    }
  };

  // Send SMS message via Supabase Edge Function
  const sendSMSMessage = async (customerPhone: string, pdfPath: string, companyName: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Kullanıcı oturumu bulunamadı');
      }

      const smsPhone = formatPhoneForSMS(customerPhone);
      if (!smsPhone) {
        throw new Error('Geçerli telefon numarası bulunamadı');
      }

      // Get public URL for the PDF so Twilio can include it in SMS
      console.log('📄 Getting PDF URL for path:', pdfPath);
      const pdfPublicUrl = await getPackagePDFUrl(pdfPath);
      console.log('✅ Generated PDF public URL:', pdfPublicUrl);
      
      // Verify the URL is accessible before sending
      try {
        const checkResponse = await fetch(pdfPublicUrl, { method: 'HEAD' });
        console.log('🔍 PDF URL accessibility check:', {
          status: checkResponse.status,
          statusText: checkResponse.statusText,
          contentType: checkResponse.headers.get('content-type')
        });
        
        if (!checkResponse.ok) {
          throw new Error(`PDF URL not accessible: ${checkResponse.status} ${checkResponse.statusText}`);
        }
      } catch (checkError) {
        console.error('❌ PDF URL check failed:', checkError);
        throw new Error(`PDF dosyası erişilebilir değil: ${checkError.message}`);
      }
      
      const requestBody = {
        to: smsPhone,
        customer_name: packageData.customerName,
        company_name: companyName,
        valid_until: packageData.validUntil || undefined, // Send ISO date string (YYYY-MM-DD)
        pdf_url: pdfPublicUrl
      };
      
      console.log('📤 Sending SMS request:', {
        ...requestBody,
        pdf_url: pdfPublicUrl.substring(0, 100) + '...' // Truncate for logging
      });
      
      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/send-whatsapp-offer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'SMS mesajı gönderilemedi');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('SMS message error:', error);
      throw error;
    }
  };

  // Debounced search for customers
  const searchCustomers = useCallback(async (searchTerm: string) => {
    if (!companyId || searchTerm.length < 2) {
      setCustomers([]);
      return;
    }

    setLoadingCustomers(true);
    try {
      const results = await customerService.searchCustomers(companyId, searchTerm, 20);
      setCustomers(results);
    } catch (error) {
      console.error('Error searching customers:', error);
      toast({
        title: "Hata",
        description: "Müşteri arama sırasında hata oluştu",
        variant: "destructive",
      });
    } finally {
      setLoadingCustomers(false);
    }
  }, [companyId, toast]);

  // Debounced search for products
  const searchProducts = useCallback(async (searchTerm: string) => {
    if (!companyId || searchTerm.length < 2) {
      setProducts([]);
      return;
    }

    setLoadingProducts(true);
    try {
      const results = await productService.searchProducts(companyId, searchTerm, 20);
      setProducts(results);
    } catch (error) {
      console.error('Error searching products:', error);
      toast({
        title: "Hata",
        description: "Ürün arama sırasında hata oluştu",
        variant: "destructive",
      });
    } finally {
      setLoadingProducts(false);
    }
  }, [companyId, toast]);

  // Effect for customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch) {
        searchCustomers(customerSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearch, searchCustomers]);

  // Effect for product search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearch) {
        searchProducts(productSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearch, searchProducts]);

  // Load package data
  const loadPackageData = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      
      // Load customers and products in parallel
      const [customersData, productsData] = await Promise.all([
        customerService.getCustomers(companyId),
        productService.getProducts(companyId)
      ]);
      
      setCustomers(customersData);
      setProducts(productsData);
      
      // Handle preselected customer for new packages
      if (isNewPackage && preselectedCustomerId) {
        const preselectedCustomer = customersData.find(c => c.id === preselectedCustomerId);
        if (preselectedCustomer) {
          const customerName = getCustomerName(preselectedCustomer);
          setPackageData(prev => ({
            ...prev,
            customerId: preselectedCustomer.id,
            customerName: customerName,
          }));
          setCustomerSearch(customerName);
        }
      }
      
      // Load package data if editing existing package
      if (!isNewPackage && id) {
        const packageDetails = await packageService.getPackageById(id, companyId);
        if (packageDetails) {
          const customerName = packageDetails.customer ? 
            getCustomerName(packageDetails.customer) : '';
          
          setPackageData({
            id: packageDetails.id,
            title: packageDetails.title || `Package ${packageDetails.id.slice(0, 8)}`,
            customerId: packageDetails.customer_id || '',
            customerName,
            items: packageDetails.package_items?.map(item => ({
              id: item.id,
              productId: item.product_id,
              name: item.product?.name || 'Unknown Product',
              code: item.product?.item_code || '',
              quantity: item.quantity,
              unit: 'pcs', // Default unit, could be enhanced
              price: item.quoted_price_each || item.product?.price || 0,
              note: '', // Note could be added to package_items table if needed
            })) || [],
            globalDiscount: packageDetails.total_discount,
            note: packageDetails.note || '',
            status: packageDetails.status,
            validUntil: packageDetails.valid_until || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            createdAt: packageDetails.created_at,
            updatedAt: packageDetails.package_datetime,
          });
          
          // Set customer search text if customer exists
          if (customerName) {
            setCustomerSearch(customerName);
          }
          
          // Calculate discount percentage if there's a global discount
          const subtotal = packageDetails.package_items?.reduce((sum, item) => 
            sum + (item.quoted_price_each || 0) * item.quantity, 0) || 0;
          if (packageDetails.total_discount > 0 && subtotal > 0) {
            setDiscountPercentage((packageDetails.total_discount / subtotal) * 100);
          }
        }
      }
    } catch (error) {
      console.error('Error loading package data:', error);
      toast({
        title: "Hata",
        description: "Veriler yüklenirken bir hata oluştu",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && !authLoading) {
      loadPackageData();
    }
  }, [companyId, authLoading, id, isNewPackage, preselectedCustomerId]);

  // Clear PDF state when package ID changes to prevent using old PDFs
  useEffect(() => {
    setGeneratedPDFPath('');
    setCompanyDataForSMS(null);
    setSmsPhone('');
    setShowPhoneModal(false);
  }, [id]);

  const handleCustomerSelect = (customer: CustomerRow) => {
    setPackageData(prev => ({
      ...prev,
      customerId: customer.id,
      customerName: getCustomerName(customer),
    }));
    setCustomerSearch(getCustomerName(customer));
    setShowCustomerResults(false);
    setShowCustomerSelect(false);
    setCustomers([]);
  };

  const handleProductSelect = (product: ProductRow) => {
    const newItem: PackageItemUI = {
      id: Date.now().toString(),
      productId: product.id,
      name: product.name,
      code: product.item_code,
      quantity: 1,
      unit: 'pcs', // Default unit
      price: product.price,
      note: ''
    };

    setPackageData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setProductSearch('');
    setShowProductResults(false);
    setShowProductSelect(false);
    setProducts([]);
  };

  const handleCodeAdd = async () => {
    if (!companyId) return;
    
    try {
      const product = await productService.getProductByCode(companyId, productCode);
      if (product) {
        handleProductSelect(product);
        setProductCode('');
        setShowCodeInput(false);
      } else {
        toast({
          title: "Ürün bulunamadı",
          description: "Bu kod ile ürün bulunamadı",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error finding product by code:', error);
      toast({
        title: "Hata",
        description: "Ürün aranırken bir hata oluştu",
        variant: "destructive",
      });
    }
  };

  const updateItem = (itemId: string, field: keyof PackageItemUI, value: any) => {
    setPackageData(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeItem = (itemId: string) => {
    setPackageData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const duplicateItem = (itemId: string) => {
    const item = packageData.items.find(i => i.id === itemId);
    if (item) {
      const newItem = { ...item, id: Date.now().toString() };
      setPackageData(prev => ({
        ...prev,
        items: [...prev.items, newItem]
      }));
    }
  };

  const calculateSubtotal = () => {
    return packageData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() - packageData.globalDiscount;
  };

  // Discount calculation functions
  const handleDiscountAmountChange = (amount: number) => {
    const subtotal = calculateSubtotal();
    setPackageData(prev => ({ ...prev, globalDiscount: amount }));
    
    if (subtotal > 0) {
      setDiscountPercentage((amount / subtotal) * 100);
    } else {
      setDiscountPercentage(0);
    }
    setDiscountType('amount');
  };

  const handleDiscountPercentageChange = (percentage: number) => {
    const subtotal = calculateSubtotal();
    const amount = (subtotal * percentage) / 100;
    
    setPackageData(prev => ({ ...prev, globalDiscount: amount }));
    setDiscountPercentage(percentage);
    setDiscountType('percentage');
  };

  const handleSave = async () => {
    if (!companyId) return;
    
    try {
      setSaving(true);
      
      const totals = packageUtils.calculateTotals(
        packageData.items.map(item => ({
          quantity: item.quantity,
          quoted_price_each: item.price,
          discount: 0 // Individual item discounts could be added later
        })),
        packageData.globalDiscount
      );

      if (isNewPackage) {
        // Create new package
        const packageInsert: PackageInsert = {
          company_id: companyId,
          customer_id: packageData.customerId || null,
          title: packageData.title,
          note: packageData.note,
          status: packageData.status,
          valid_until: packageData.validUntil,
          total_discount: totals.total_discount,
          total_gross: totals.total_gross,
          total_net: totals.total_net,
          package_datetime: new Date().toISOString(),
        };

        const newPackageId = await packageService.createPackage(packageInsert);
        
        // Add package items
        if (packageData.items.length > 0) {
          const itemsToInsert: PackageItemInsert[] = packageData.items.map(item => ({
            company_id: companyId,
            package_id: newPackageId,
            product_id: item.productId,
            quantity: item.quantity,
            quoted_price_each: item.price,
            discount: 0,
            line_total_gross: item.price * item.quantity,
            line_total_net: item.price * item.quantity,
          }));

          await packageItemService.addPackageItems(itemsToInsert);
        }

        // Log activity
        if (companyId) {
          await logActivity({
            action: 'create',
            entity: 'package',
            entityId: newPackageId,
            detail: {
              title: packageData.title,
              customer_id: packageData.customerId,
              item_count: packageData.items.length,
              total_net: totals.total_net,
            },
            locationId: currentLocation?.id || null,
            companyId,
            userId: user?.id || null,
          });
        }

        toast({
          title: "Paket Kaydedildi",
          description: "Paket başarıyla oluşturuldu",
        });
        
        // Navigate to the new package
        navigate(`/packages/${newPackageId}`);
      } else {
        // Update existing package
        const packageUpdate: PackageUpdate = {
          customer_id: packageData.customerId || null,
          title: packageData.title,
          note: packageData.note,
          status: packageData.status,
          valid_until: packageData.validUntil,
          total_discount: totals.total_discount,
          total_gross: totals.total_gross,
          total_net: totals.total_net,
          package_datetime: new Date().toISOString(),
        };

        // Update package metadata
        await packageService.updatePackage(packageData.id!, packageUpdate);
        
        // Synchronize package items (add, update, remove as needed)
        await packageItemService.syncPackageItems(
          packageData.id!,
          companyId,
          packageData.items.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            price: item.price,
          }))
        );

        // Log activity
        if (companyId) {
          await logActivity({
            action: 'update',
            entity: 'package',
            entityId: packageData.id!,
            detail: {
              title: packageData.title,
              changes: packageUpdate,
              item_count: packageData.items.length,
            },
            locationId: currentLocation?.id || null,
            companyId,
            userId: user?.id || null,
          });
        }
        
        toast({
          title: "Paket Güncellendi",
          description: "Paket ve ürünler başarıyla güncellendi",
        });
      }
    } catch (error) {
      console.error('Error saving package:', error);
      toast({
        title: "Hata",
        description: "Paket kaydedilirken bir hata oluştu",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendToSale = () => {
    if (packageData.items.length === 0) {
      toast({
        title: "Hata",
        description: "Satışa göndermek için en az bir ürün eklemelisiniz",
        variant: "destructive",
      });
      return;
    }

    // Prepare package data for sale
    const saleData = {
      customer: packageData.customerId ? {
        id: packageData.customerId,
        name: packageData.customerName,
      } : null,
      items: packageData.items.map(item => ({
        id: item.id,
        productId: item.productId,
        name: item.name,
        code: item.code,
        price: item.price,
        quantity: item.quantity,
        discount: 0, // Individual item discounts
        subtotal: item.price * item.quantity,
        onHold: false,
        note: item.note,
      })),
      globalDiscount: packageData.globalDiscount,
      notes: packageData.note,
      fromPackage: true,
      packageId: packageData.id,
    };

    // Navigate to new sale with pre-filled data
    navigate('/sales/new', { state: saleData });
  };

  const handleQRScan = () => {
    setShowQRScan(true);
  };
  
  const handleQRProductFound = useCallback((product: ProductData) => {
    // Convert ProductData to ProductRow format for handleProductSelect
    const productRow = {
      id: product.id,
      name: product.name,
      item_code: product.item_code,
      price: product.price,
      quantity_on_hand: product.quantity_on_hand,
      category: product.category,
      // Add other required ProductRow fields with defaults
      company_id: companyId || '',
      current_cost: 0,
      initial_cost: 0,
      entry_date: '',
      meta: {},
      created_at: '',
      updated_at: ''
    };
    
    handleProductSelect(productRow);
  }, [companyId, handleProductSelect]);

  const handleGenerateAndSharePDF = async () => {
    if (!companyId || !packageData.id) {
      toast({
        title: "Hata",
        description: "Paket kaydedilmeden PDF oluşturulamaz",
        variant: "destructive",
      });
      return;
    }

    try {
      setGeneratingPDF(true);
      
      // Clear any previous PDF path to ensure we use the latest one
      setGeneratedPDFPath('');
      setCompanyDataForSMS(null);
      
      // Get customer and company details in parallel
      const [customerDetails, companyDetails] = await Promise.all([
        // Get customer details
        packageData.customerId ? (async () => {
          const customers = await customerService.getCustomers(companyId);
          const customer = customers.find(c => c.id === packageData.customerId);
          if (customer) {
            return {
              name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Adsız Müşteri',
              phone: customer.phone || undefined,
              email: customer.email || undefined,
            };
          }
          return null;
        })() : Promise.resolve(null),
        
        // Get company details
        fetchCompanyForPDF(companyId)
      ]);

      // Prepare PDF data
      const pdfData: PackagePDFData = {
        id: packageData.id,
        title: packageData.title,
        validUntil: packageData.validUntil,
        customer: customerDetails,
        company: companyDetails,
        items: packageData.items.map(item => ({
          name: item.name,
          code: item.code,
          quantity: item.quantity,
          price: item.price,
          total: item.price * item.quantity
        })),
        subtotal: calculateSubtotal(),
        discount: packageData.globalDiscount,
        total: calculateTotal(),
        note: packageData.note,
        createdAt: packageData.createdAt
      };

      // Generate PDF
      const pdfBlob = await generatePackagePDF(pdfData);
      
      // Upload to storage
      const filePath = await uploadPackagePDF(pdfBlob, packageData.id, companyId, packageData.title);
      
      toast({
        title: "PDF Oluşturuldu! 📄",
        description: "Paket teklifi PDF'i başarıyla oluşturuldu. SMS gönderimi için telefon numarasını onaylayın.",
      });

      // Store data for SMS modal with timestamp validation
      setGeneratedPDFPath(filePath);
      setCompanyDataForSMS(companyDetails);
      
      // Log the PDF generation for debugging
      console.log('PDF generated successfully:', {
        filePath,
        packageId: packageData.id,
        timestamp: new Date().toISOString()
      });
      
      // Pre-fill phone number if customer has one
      if (customerDetails?.phone) {
        setSmsPhone(customerDetails.phone);
      } else {
        setSmsPhone('');
      }
      
      // Show phone confirmation modal
      setShowPhoneModal(true);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        errorType: typeof error,
        errorObject: error
      });
      
      const errorMessage = error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu';
      
      toast({
        title: "Hata",
        description: `PDF oluşturulurken bir hata oluştu: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Actually send the SMS (called after KVKK consent is granted)
  const executeSMS = async () => {
    if (!queuedSMSData) {
      return;
    }

    try {
      setSendingSMS(true);
      
      // Log the SMS sending attempt for debugging
      console.log('Sending SMS with PDF:', {
        pdfPath: queuedSMSData.pdfPath,
        phone: queuedSMSData.phone,
        packageId: packageData.id,
        timestamp: new Date().toISOString()
      });
      
      await sendSMSMessage(queuedSMSData.phone, queuedSMSData.pdfPath, queuedSMSData.companyName);
      
      toast({
        title: "SMS Mesajı Gönderildi! 📱",
        description: `Paket teklifi ${queuedSMSData.phone} numarasına SMS ile gönderildi`,
      });
      
      setShowPhoneModal(false);
      setShowKvkkModal(false);
      
      // Clear the PDF path after successful sending to prevent reuse
      setGeneratedPDFPath('');
      setCompanyDataForSMS(null);
      setQueuedSMSData(null);
    } catch (smsError) {
      console.error('SMS sending failed:', smsError);
      toast({
        title: "SMS Gönderimi Başarısız",
        description: smsError instanceof Error ? smsError.message : "SMS mesajı gönderilemedi",
        variant: "destructive",
      });
    } finally {
      setSendingSMS(false);
    }
  };

  const handleSendSMSFromModal = async () => {
    if (!smsPhone.trim()) {
      toast({
        title: "Hata",
        description: "Telefon numarası gerekli",
        variant: "destructive",
      });
      return;
    }

    if (!generatedPDFPath || !companyDataForSMS?.name) {
      toast({
        title: "Hata",
        description: "PDF veya şirket bilgileri eksik",
        variant: "destructive",
      });
      return;
    }

    // Additional validation to ensure PDF path is valid and recent
    if (!generatedPDFPath.includes(packageData.id)) {
      toast({
        title: "Hata",
        description: "PDF dosyası bu paket ile eşleşmiyor. Lütfen PDF'i yeniden oluşturun.",
        variant: "destructive",
      });
      return;
    }

    // Check KVKK consent before sending
    const consentStatus = await checkKvkkConsent(smsPhone);
    
    if (consentStatus === 'granted') {
      // Consent is granted, send SMS immediately
      setQueuedSMSData({
        phone: smsPhone,
        pdfPath: generatedPDFPath,
        companyName: companyDataForSMS.name,
      });
      await executeSMS();
    } else if (consentStatus === 'denied') {
      // Consent is denied, show error
      toast({
        title: "KVKK Onayı Reddedilmiş",
        description: "Müşteri KVKK onayını reddetmiş. SMS gönderilemez.",
        variant: "destructive",
      });
    } else {
      // No consent record or withdrawn - queue SMS and show KVKK modal
      setQueuedSMSData({
        phone: smsPhone,
        pdfPath: generatedPDFPath,
        companyName: companyDataForSMS.name,
      });
      setShowPhoneModal(false);
      setShowKvkkModal(true);
    }
  };

  // Handle KVKK consent granted callback
  const handleKvkkConsentGranted = async () => {
    if (queuedSMSData) {
      await executeSMS();
    }
  };


  if (authLoading || loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-4">
          {/* Back button and title */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/packages')}
              className="gap-2 shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Paketlere Dön</span>
              <span className="sm:hidden">Geri</span>
            </Button>
            
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold truncate">
                {isNewPackage ? 'Yeni Paket' : packageData.title}
              </h1>
              {!isNewPackage && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <Badge variant={packageData.status === 'offered' ? 'default' : 'secondary'} className="shrink-0">
                    {packageUtils.formatStatus(packageData.status).label}
                  </Badge>
                  {packageData.validUntil && (
                    <Badge variant="outline" className="gap-1 shrink-0">
                      <Calendar className="w-3 h-3" />
                      <span className="hidden sm:inline">Geçerli:</span>
                      <span className="sm:hidden">Geç:</span>
                      <span className="truncate">{new Date(packageData.validUntil).toLocaleDateString('tr-TR')}</span>
                    </Badge>
                  )}
                  <span className="text-xs sm:text-sm text-muted-foreground truncate">
                    <span className="hidden sm:inline">Oluşturuldu</span>
                    <span className="sm:hidden">Oluş:</span> {new Date(packageData.createdAt).toLocaleDateString('tr-TR')}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* Action buttons - responsive layout */}
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <div className="grid grid-cols-1 sm:flex sm:gap-2 space-y-2 sm:space-y-0">
              <Button variant="outline" onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                <span className="hidden sm:inline">Taslak Kaydet</span>
                <span className="sm:hidden">Kaydet</span>
              </Button>
              <Button 
                variant="outline" 
                onClick={handleGenerateAndSharePDF}
                disabled={generatingPDF || !packageData.id}
                className="w-full sm:w-auto"
              >
                {generatingPDF ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <MessageCircle className="w-4 h-4 mr-2" />
                )}
                {generatingPDF ? (
                  <>
                    <span className="hidden sm:inline">PDF Oluşturuluyor...</span>
                    <span className="sm:hidden">PDF...</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">SMS ile PDF Gönder</span>
                    <span className="sm:hidden">SMS</span>
                  </>
                )}
              </Button>
              <Button onClick={handleSendToSale} className="w-full sm:w-auto">
                <ShoppingCart className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Satışa Gönder</span>
                <span className="sm:hidden">Satış</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6">
          {/* Main Content */}
          <div className="xl:col-span-2 space-y-4 lg:space-y-6">
            {/* Package Info */}
            <Card>
              <CardHeader>
                <CardTitle>Paket Bilgileri</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="packageName">Paket Adı</Label>
                  <Input
                    id="packageName"
                    value={packageData.title}
                    onChange={(e) => setPackageData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Paket adını girin..."
                  />
                </div>

                <div>
                  <Label htmlFor="validUntil">Geçerlilik Tarihi</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="validUntil"
                      type="date"
                      value={packageData.validUntil}
                      onChange={(e) => setPackageData(prev => ({ ...prev, validUntil: e.target.value }))}
                      className="pl-10"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Bu tarihe kadar teklif geçerli olacaktır
                  </p>
                </div>

                <div>
                  <Label>Müşteri</Label>
                  <div className="relative">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Müşteri ara..."
                          value={customerSearch}
                          onChange={(e) => {
                            setCustomerSearch(e.target.value);
                            setShowCustomerResults(e.target.value.length > 0);
                            if (e.target.value === '') {
                              setPackageData(prev => ({ ...prev, customerId: '', customerName: '' }));
                              setCustomers([]);
                            }
                          }}
                          onFocus={() => setShowCustomerResults(customerSearch.length > 0)}
                          onBlur={() => setTimeout(() => setShowCustomerResults(false), 150)}
                          className="pl-10"
                        />
                        
                        {showCustomerResults && (
                          <div className="absolute top-full mt-1 w-full bg-popover border border-border rounded-md shadow-lg z-10 max-h-60 overflow-y-auto backdrop-blur-sm">
                            {loadingCustomers ? (
                              <div className="p-4 text-muted-foreground text-center">
                                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                                Müşteriler aranıyor...
                              </div>
                            ) : customers.length > 0 ? (
                              customers.map((customer) => (
                                <div
                                  key={customer.id}
                                  className="flex items-center gap-3 p-4 hover:bg-accent cursor-pointer touch-manipulation"
                                  onClick={() => handleCustomerSelect(customer)}
                                >
                                  <Avatar className="w-8 h-8 shrink-0">
                                    <AvatarFallback className="bg-accent/20 text-accent text-xs">
                                      {getCustomerName(customer).split(' ').map(n => n[0]).join('')}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium truncate">{getCustomerName(customer)}</p>
                                    <p className="text-sm text-muted-foreground truncate">{customer.email || customer.phone}</p>
                                  </div>
                                </div>
                              ))
                            ) : customerSearch.length >= 2 ? (
                              <div className="p-4 text-muted-foreground text-center">
                                Müşteri bulunamadı
                              </div>
                            ) : (
                              <div className="p-4 text-muted-foreground text-center">
                                En az 2 karakter girin
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {packageData.customerId && (
                      <Card className="mt-2 bg-accent/5">
                        <CardContent className="p-3">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-8 h-8 shrink-0">
                              <AvatarFallback className="bg-accent/20 text-accent text-xs">
                                {packageData.customerName.split(' ').map(n => n[0]).join('')}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <h4 className="font-medium truncate">{packageData.customerName}</h4>
                              <p className="text-sm text-muted-foreground">Seçili Müşteri</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>


              </CardContent>
            </Card>

            {/* Items */}
            <Card>
              <CardHeader className="space-y-4">
                <CardTitle>Ürünler</CardTitle>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="outline" size="sm" onClick={handleQRScan} className="flex-1 sm:flex-none">
                    <QrCode className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">QR Tara</span>
                    <span className="sm:hidden">QR</span>
                  </Button>
                  
                  <Dialog open={showCodeInput} onOpenChange={setShowCodeInput}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
                        <Search className="w-4 h-4 mr-2" />
                        <span className="hidden sm:inline">Kod ile Ekle</span>
                        <span className="sm:hidden">Kod</span>
                      </Button>
                    </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Ürün Kodu ile Ekle</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <Input
                            placeholder="Ürün kodunu girin..."
                            value={productCode}
                            onChange={(e) => setProductCode(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCodeAdd()}
                          />
                          <div className="flex gap-2">
                            <Button variant="outline" className="flex-1" onClick={() => setShowCodeInput(false)}>
                              İptal
                            </Button>
                            <Button className="flex-1" onClick={handleCodeAdd}>
                              Ürün Ekle
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Dialog open={showProductSelect} onOpenChange={setShowProductSelect}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="flex-1 sm:flex-none">
                          <Plus className="w-4 h-4 mr-2" />
                          <span className="hidden sm:inline">Ürün Gözat</span>
                          <span className="sm:hidden">Gözat</span>
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[80vh]">
                        <DialogHeader>
                          <DialogTitle>Ürün Ara ve Seç</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Ürün ara..."
                              value={productSearch}
                              onChange={(e) => {
                                setProductSearch(e.target.value);
                                setShowProductResults(e.target.value.length > 0);
                                if (e.target.value === '') {
                                  setProducts([]);
                                }
                              }}
                              onFocus={() => setShowProductResults(productSearch.length > 0)}
                              className="pl-10"
                            />
                          </div>
                          
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {loadingProducts ? (
                              <div className="p-4 text-muted-foreground text-center">
                                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                                Ürünler aranıyor...
                              </div>
                            ) : products.length > 0 ? (
                              products.map((product) => (
                                <div
                                  key={product.id}
                                  className="flex items-center gap-3 p-4 border rounded cursor-pointer hover:bg-accent/50 touch-manipulation"
                                  onClick={() => handleProductSelect(product)}
                                >
                                  <Package className="w-8 h-8 text-muted-foreground shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium truncate">{product.name}</p>
                                    <p className="text-sm text-muted-foreground truncate">
                                      {product.item_code} • ₺{product.price.toLocaleString('tr-TR')}
                                    </p>
                                  </div>
                                </div>
                              ))
                            ) : productSearch.length >= 2 ? (
                              <div className="p-4 text-muted-foreground text-center">
                                Ürün bulunamadı
                              </div>
                            ) : (
                              <div className="p-4 text-muted-foreground text-center">
                                Arama için en az 2 karakter girin
                              </div>
                            )}
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
              </CardHeader>
              <CardContent>
                {packageData.items.length === 0 ? (
                  <div className="text-center py-8 sm:py-12 text-muted-foreground">
                    <Package className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 opacity-50" />
                    <h3 className="font-medium mb-2 text-sm sm:text-base">Hiç ürün eklenmedi</h3>
                    <p className="text-xs sm:text-sm">Başlamak için QR tara veya kod ile ekle</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {packageData.items.map((item) => (
                      <div key={item.id} className="p-3 sm:p-4 border rounded-lg space-y-3">
                        {/* Product header */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium truncate">{item.name}</h4>
                            <p className="text-sm text-muted-foreground truncate">{item.code}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => duplicateItem(item.id)}
                              className="h-8 w-8 p-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(item.id)}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Product controls - responsive layout */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {/* Quantity */}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Adet</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') {
                                    updateItem(item.id, 'quantity', 1);
                                  } else {
                                    const numValue = parseInt(value);
                                    if (!isNaN(numValue) && numValue >= 1) {
                                      updateItem(item.id, 'quantity', numValue);
                                    }
                                  }
                                }}
                                className="flex-1"
                                min="1"
                                placeholder="1"
                              />
                              <span className="text-sm text-muted-foreground">{item.unit}</span>
                            </div>
                          </div>
                          
                          {/* Price */}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Birim Fiyat (₺)</Label>
                            <Input
                              type="number"
                              value={item.price === 0 ? '' : item.price}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  updateItem(item.id, 'price', 0);
                                } else {
                                  const numValue = parseFloat(value);
                                  if (!isNaN(numValue)) {
                                    updateItem(item.id, 'price', numValue);
                                  }
                                }
                              }}
                              min="0"
                              step="0.01"
                              placeholder="0"
                            />
                          </div>
                          
                          {/* Total */}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Toplam</Label>
                            <div className="flex items-center h-9 px-3 bg-muted/50 rounded-md">
                              <span className="text-sm font-medium">
                                ₺{(item.price * item.quantity).toLocaleString('tr-TR')}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Note input */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Not</Label>
                          <Input
                            placeholder="Bu ürün için not ekle..."
                            value={item.note}
                            onChange={(e) => updateItem(item.id, 'note', e.target.value)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary Sidebar */}
          <div className="space-y-4 lg:space-y-6 xl:sticky xl:top-4">
            <Card>
              <CardHeader>
                <CardTitle>Özet</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4">
                <div className="flex justify-between">
                  <span>Ara Toplam:</span>
                  <span>₺{calculateSubtotal().toLocaleString('tr-TR')}</span>
                </div>
                
                <div className="space-y-3">
                  <Label>İndirim</Label>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="discount-percentage" className="text-sm text-muted-foreground">
                        Yüzde (%)
                      </Label>
                      <div className="relative">
                        <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="discount-percentage"
                          type="number"
                          value={discountPercentage === 0 ? '' : discountPercentage.toFixed(2)}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              handleDiscountPercentageChange(0);
                            } else {
                              const numValue = parseFloat(value);
                              if (!isNaN(numValue)) {
                                handleDiscountPercentageChange(numValue);
                              }
                            }
                          }}
                          placeholder="0"
                          className="pl-10"
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="discount-amount" className="text-sm text-muted-foreground">
                        Tutar (₺)
                      </Label>
                      <div className="relative">
                        <TrendingUp className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="discount-amount"
                          type="number"
                          value={packageData.globalDiscount === 0 ? '' : packageData.globalDiscount}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              handleDiscountAmountChange(0);
                            } else {
                              const numValue = parseFloat(value);
                              if (!isNaN(numValue)) {
                                handleDiscountAmountChange(numValue);
                              }
                            }
                          }}
                          placeholder="0"
                          className="pl-10"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-xs text-muted-foreground">
                    Yüzde veya tutar girin - diğeri otomatik hesaplanacak
                  </div>
                </div>
                
                <Separator />
                
                <div className="flex justify-between text-base sm:text-lg font-semibold">
                  <span>Toplam:</span>
                  <span className="text-accent">₺{calculateTotal().toLocaleString('tr-TR')}</span>
                </div>
                
                <div className="pt-2 sm:pt-4">
                  <Label htmlFor="note">Genel Not</Label>
                  <Textarea
                    id="note"
                    value={packageData.note}
                    onChange={(e) => setPackageData(prev => ({ ...prev, note: e.target.value }))}
                    placeholder="Genel notlar ekle..."
                    rows={3}
                    className="min-h-[80px] resize-none"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* QR Scan Modal */}
        <WorkingQRScanModal
          open={showQRScan}
          onOpenChange={setShowQRScan}
          onProductFound={handleQRProductFound}
          title="Pakete Ürün Ekle"
        />

        {/* KVKK Consent Modal */}
        {showKvkkModal && queuedSMSData && companyId && (
          <KvkkConsentModal
            open={showKvkkModal}
            onOpenChange={setShowKvkkModal}
            phone={formatPhoneForKvkk(queuedSMSData.phone)}
            companyId={companyId}
            onConsentGranted={handleKvkkConsentGranted}
          />
        )}

        {/* SMS Phone Number Modal */}
        <Dialog open={showPhoneModal} onOpenChange={setShowPhoneModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                📱 SMS Gönderimi
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="sms-phone">Telefon Numarası</Label>
                <Input
                  id="sms-phone"
                  type="tel"
                  placeholder="+90 5XX XXX XX XX"
                  value={smsPhone}
                  onChange={(e) => setSmsPhone(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Paket teklifi bu numaraya SMS ile gönderilecek (PDF linki mesajda yer alacak)
                </p>
              </div>

              {packageData.customerName && (
                <div className="p-3 bg-accent/10 rounded-md">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{packageData.customerName}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Seçili müşteri - farklı bir numaraya da gönderebilirsiniz
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Button 
                  onClick={handleSendSMSFromModal}
                  disabled={sendingSMS || !smsPhone.trim()}
                  className="w-full"
                >
                  {sendingSMS ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      SMS Gönderiliyor...
                    </>
                  ) : (
                    <>
                      📱 SMS Gönder
                    </>
                  )}
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={() => setShowPhoneModal(false)}
                  className="w-full"
                >
                  İptal Et
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Bottom padding for mobile scrolling */}
        <div className="h-4 sm:hidden" />
      </div>
    </Layout>
  );
}